// Campaign Detail page component for InfluenceHub MVP
// Shows detailed information about a specific campaign for admin review
// Allows admin to approve or reject campaigns with full context

import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const CampaignDetail = ({ campaignId, onNavigate }) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Fetch campaign details
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        console.log('🔄 Fetching campaign details for:', campaignId);
        
        if (!db) {
          throw new Error('Firebase database not initialized');
        }

        const campaignRef = doc(db, 'campaigns', campaignId);
        const campaignSnap = await getDoc(campaignRef);
        
        if (campaignSnap.exists()) {
          const campaignData = {
            id: campaignSnap.id,
            ...campaignSnap.data(),
            createdAt: campaignSnap.data().createdAt?.toDate() || new Date(),
            deadline: campaignSnap.data().deadline || null, // Can be string or Timestamp
            updatedAt: campaignSnap.data().updatedAt?.toDate() || new Date()
          };
          setCampaign(campaignData);
          console.log('✅ Campaign loaded:', campaignData);
        } else {
          setError('Campaign not found');
        }
      } catch (error) {
        console.error('❌ Error fetching campaign:', error);
        setError('Failed to load campaign details');
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  // Update campaign status
  const updateCampaignStatus = async (status) => {
    try {
      setUpdating(true);
      console.log(`🔄 Updating campaign ${campaignId} to ${status}...`);
      
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        status,
        updatedAt: serverTimestamp()
      });

      console.log(`✅ Campaign ${status} successfully`);
      alert(`Campaign ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      
      // Navigate back to admin dashboard
      onNavigate('admin');
      
    } catch (error) {
      console.error(`❌ Error updating campaign:`, error);
      alert(`Failed to update campaign: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Campaign</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => onNavigate('admin')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📋</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Campaign Not Found</h1>
          <p className="text-gray-600 mb-6">The requested campaign could not be found.</p>
          <button
            onClick={() => onNavigate('admin')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get campaign title - use new field or fallback to legacy
  const campaignTitle = campaign.title || campaign.campaignName || campaign.name || 'Untitled Campaign';
  const campaignBio = campaign.bio || campaign.brief || campaign.description || '';
  const budget = campaign.budgetUSD ?? campaign.budget ?? null;
  const collaborationType = campaign.collaborationType || campaign.goal || 'Not specified';
  const hasPhysicalGift = campaign.hasPhysicalGift ?? false;
  const shippingFromCountry = campaign.shippingFromCountry || campaign.shippingFrom || null;
  const shippingNotes = campaign.shippingNotes || null;

  // Determine status badge
  const getStatusBadge = () => {
    const status = campaign.status || 'open';
    if (status === 'open' || status === 'pending_approval') {
      return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">⏳ Pending Approval</span>;
    } else if (status === 'approved' || status === 'active') {
      return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">✅ Approved</span>;
    } else {
      return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">❌ Rejected</span>;
    }
  };

  const canApprove = campaign.status === 'pending_approval';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => onNavigate('admin')}
                className="text-gray-500 hover:text-gray-700 mr-4"
              >
                ← Back to Admin Dashboard
              </button>
              <h1 className="text-xl font-bold text-gray-900">Campaign Review</h1>
            </div>
            <div className="flex items-center space-x-4">
              {getStatusBadge()}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          
          {/* Campaign Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{campaignTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Brand</h3>
                <p className="mt-1 text-lg font-semibold text-gray-900">{campaign.brandName || 'Not specified'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Budget</h3>
                <p className="mt-1 text-lg font-semibold text-green-600">{formatCurrency(budget)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Collaboration Type</h3>
                <p className="mt-1 text-lg font-semibold text-gray-900">{collaborationType}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Content Submission Deadline</h3>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {typeof campaign.deadline === 'string' 
                    ? campaign.deadline 
                    : formatDate(campaign.deadline)}
                </p>
              </div>
            </div>
          </div>

          {/* Campaign Bio */}
          {campaignBio && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Bio</h3>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{campaignBio}</p>
              </div>
            </div>
          )}

          {/* Product Link */}
          {campaign.productLink && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Link</h3>
              <a 
                href={campaign.productLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-500 break-all underline"
              >
                {campaign.productLink}
              </a>
            </div>
          )}

          {/* Physical Gift Information */}
          {hasPhysicalGift && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Physical Gift Information</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Has Physical Gift</h4>
                  <p className="text-gray-900">Yes</p>
                </div>
                {shippingFromCountry && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Shipping From Country</h4>
                    <p className="text-gray-900">{shippingFromCountry}</p>
                  </div>
                )}
                {shippingNotes && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Shipping Notes</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{shippingNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Revision Policy */}
          {campaign.revisionPolicy && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revision Policy</h3>
              <p className="text-gray-700">{campaign.revisionPolicy}</p>
            </div>
          )}

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Details</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Submitted</h4>
                <p className="mt-1 text-gray-900">{formatDate(campaign.createdAt)}</p>
              </div>
              {campaign.updatedAt && campaign.updatedAt !== campaign.createdAt && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Last Updated</h4>
                  <p className="mt-1 text-gray-900">{formatDate(campaign.updatedAt)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {canApprove && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Actions</h3>
              <div className="flex space-x-4">
                <button
                  onClick={() => updateCampaignStatus('approved')}
                  disabled={updating}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    '✅ Approve Campaign'
                  )}
                </button>
                <button
                  onClick={() => updateCampaignStatus('rejected')}
                  disabled={updating}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    '❌ Reject Campaign'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDetail;
